import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  TextField,
  Box,
  FormControl,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import FilledButton from './FilledButton';
import OutlinedButton from './OutlinedButton';
import EditIcon from '@mui/icons-material/Edit';
import CancelIcon from '@mui/icons-material/Cancel';
import { fetchDepartments, fetchCourses } from '../services/api';
import { formatPrevSubmission } from '../utils/formatPrevSubmission';
import IconOnlyChip from './IconOnlyChip';

const EditCourseAdminDialog = ({ open, onClose, course: courseProp, onResubmit }) => {
  const course = courseProp ?? {};
  const [editShort, setEditShort] = useState(false);
  const [editLong, setEditLong] = useState(false);
  const [editDept, setEditDept] = useState(false);
  const [shortName, setShortName] = useState('');
  const [longName, setLongName] = useState('');
  const [deptId, setDeptId] = useState('');
  const [shortDirty, setShortDirty] = useState(false);
  const [longDirty, setLongDirty] = useState(false);
  const [deptDirty, setDeptDirty] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [courses, setCourses] = useState([]);
  const [shortNameError, setShortNameError] = useState('');
  const [longNameError, setLongNameError] = useState('');
  const [confirmed, setConfirmed] = useState(false);

  const toggleEditDept = () => {
    setEditDept((prev) => {
      if (prev) {
        setDeptId(courseProp?.department?.id || '');
        setDeptDirty(false);
      }
      return !prev;
    });
  };

  const toggleEditShort = () => {
    setEditShort((prev) => {
      if (prev) {
        setShortName('');
        setShortNameError('');
        setShortDirty(false);
      }
      return !prev;
    });
  };

  const toggleEditLong = () => {
    setEditLong((prev) => {
      if (prev) {
        setLongName('');
        setLongNameError('');
        setLongDirty(false);
      }
      return !prev;
    });
  };

  useEffect(() => {
    if (open) {
      setEditShort(false);
      setEditLong(false);
      setEditDept(false);
      setShortName('');
      setLongName('');
      setShortDirty(false);
      setLongDirty(false);
      setDeptDirty(false);
      setShortNameError('');
      setLongNameError('');
      setConfirmed(false);
      const instId = courseProp?.department?.institution?.id;
      setDeptId(courseProp?.department?.id || '');
      if (instId) {
        fetchDepartments(instId)
          .then((res) => setDepartments(res || []))
          .catch(() => setDepartments([]));
        fetchCourses(instId)
          .then((res) => {
            const list = res.results || res;
            setCourses(list || []);
          })
          .catch(() => setCourses([]));
      } else {
        setDepartments([]);
        setCourses([]);
      }
    }
  }, [open, courseProp]);

  useEffect(() => {
    if (!shortName) {
      setShortNameError('');
    } else {
      const dup = courses.some(
        (c) =>
          c.id !== course.id &&
          c.short_name?.toLowerCase().trim() ===
            shortName.trim().toLowerCase()
      );
      setShortNameError(dup ? 'Short name already exists' : '');
    }
  }, [shortName, courses, course.id]);

  useEffect(() => {
    if (!longName) {
      setLongNameError('');
    } else {
      const dup = courses.some(
        (c) =>
          c.id !== course.id &&
          c.long_name?.toLowerCase().trim() ===
            longName.trim().toLowerCase()
      );
      setLongNameError(dup ? 'Long name already exists' : '');
    }
  }, [longName, courses, course.id]);

  const changed =
    (editDept && deptDirty && deptId && deptId !== (course.department?.id || '')) ||
    (editShort && shortDirty && !shortNameError && shortName.trim() && shortName.trim() !== course.short_name) ||
    (editLong && longDirty && !longNameError && longName.trim() && longName.trim() !== course.long_name);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!courseProp) return;
    const payload = {};
    if (editDept && deptDirty && deptId !== (course.department?.id || '')) {
      payload.department = deptId;
    }
    if (
      editShort &&
      shortDirty &&
      !shortNameError &&
      shortName.trim() &&
      shortName.trim() !== course.short_name
    ) {
      payload.short_name = shortName.trim();
    }
    if (
      editLong &&
      longDirty &&
      !longNameError &&
      longName.trim() &&
      longName.trim() !== course.long_name
    ) {
      payload.long_name = longName.trim();
    }
    if (Object.keys(payload).length === 0) {
      onClose();
      return;
    }
    if (onResubmit) onResubmit(course.id, payload);
  };

  const renderField = (
    label,
    current,
    editing,
    toggle,
    value,
    setValue,
    dirty,
    setDirty,
    error = '',
    setError
  ) => (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
        {label}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography>{current || '—'}</Typography>
        <IconOnlyChip icon={<EditIcon fontSize="small" />} onClick={toggle} />
      </Box>
      {editing && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
          <TextField
            size="small"
            value={value}
            onChange={(e) => {
              setValue(e.target.value);
              if (setDirty) setDirty(true);
            }}
            placeholder="New Value"
            error={!!error}
            helperText={error}
          />
          <IconOnlyChip icon={<CancelIcon fontSize="small" />} onClick={toggle} />
        </Box>
      )}
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ component: 'form', onSubmit: handleSubmit }}
    >
      <DialogTitle>Edit Course</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            Department
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography>{course?.department?.name || '—'}</Typography>
            <IconOnlyChip icon={<EditIcon fontSize="small" />} onClick={toggleEditDept} />
          </Box>
          {editDept && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <Select
                  value={deptId}
                  onChange={(e) => {
                    setDeptId(e.target.value);
                    setDeptDirty(true);
                  }}
                >
                  {departments.map((d) => (
                    <MenuItem key={d.id} value={d.id}>
                      {d.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <IconOnlyChip icon={<CancelIcon fontSize="small" />} onClick={toggleEditDept} />
            </Box>
          )}
        </Box>
        {renderField(
          'Short Name',
          course.short_name,
          editShort,
          toggleEditShort,
          shortName,
          setShortName,
          shortDirty,
          setShortDirty,
          shortNameError,
          setShortNameError
        )}
        {renderField(
          'Long Name',
          course.long_name,
          editLong,
          toggleEditLong,
          longName,
          setLongName,
          longDirty,
          setLongDirty,
          longNameError,
          setLongNameError
        )}
        {course.rejection_reason && (
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              Reason for Rejection
            </Typography>
            <Typography>{course.rejection_reason}</Typography>
          </Box>
        )}
        {course.rejection_reason && course.previous_submission && (
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              Previous Submission
            </Typography>
            <Typography>{formatPrevSubmission(course.previous_submission)}</Typography>
          </Box>
        )}
      </DialogContent>
      <FormControlLabel
        control={
          <Checkbox
            checked={confirmed}
            onChange={(e) => setConfirmed(e.target.checked)}
          />
        }
        label="I confirm that by submitting this edit I'm transferring the ownership of the submission to myself."
        sx={{ mx: 3 }}
      />
      <DialogActions>
        <OutlinedButton onClick={onClose} text="Cancel" color="info" />
        <FilledButton
          type="submit"
          disabled={!confirmed || !changed}
          text="Resubmit"
          color="info"
        />
      </DialogActions>
    </Dialog>
  );
};

export default EditCourseAdminDialog;
